import React, { useState } from 'react';
import { formatTimestampWithTimezone } from '../../utils/timeHelpers';
import { MessageCircle } from 'lucide-react';
import ConfirmationModal from '../ConfirmationModal';

interface PrimaryContactUser {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number?: string;
  country_code?: string;
  job_title?: string;
  department?: string;
  updated_at?: string;
}

interface PrimaryContactCardProps {
  primaryContact: PrimaryContactUser | null;
  organizationId: string;
  onStartConversation: (conversationId: string) => void;
  accessToken: string;
  setUserAndToken: (user: any, token: string) => void;
}

const PrimaryContactCard: React.FC<PrimaryContactCardProps> = ({ 
  primaryContact, 
  organizationId, 
  onStartConversation,
  accessToken,
  setUserAndToken
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isExistingConversation, setIsExistingConversation] = useState(false);

  const handleStartConversation = async () => {
    if (!primaryContact) return;

    // Check if phone number exists
    if (!primaryContact.phone_number) {
      setErrorMessage('We only support contacting via WhatsApp using the phone number. Please add a phone number to the primary contact.');
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);

    try {
      // For now, we'll assume the phone number has WhatsApp
      // In a real implementation, you might want to verify this with WhatsApp Business API
      const fullPhoneNumber = primaryContact.country_code 
        ? `${primaryContact.country_code}${primaryContact.phone_number}`
        : primaryContact.phone_number;

      // Create or get existing conversation
      const { conversationService } = await import('../../services/conversationService');
      const conversation = await conversationService.createConversation(
        {
          organization_id: organizationId,
          user_id: primaryContact.id,
          pic_contact: fullPhoneNumber,
          pic_name: `${primaryContact.first_name} ${primaryContact.last_name}`,
          channel: 'whatsapp'
        },
        accessToken,
        setUserAndToken
      );

      // Navigate to WhatsApp manager with the conversation
      onStartConversation(conversation.id);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to start conversation');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 self-start">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Primary Contact</h3>
        {primaryContact && (
          <button
            onClick={handleStartConversation}
            disabled={isLoading}
            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Start WhatsApp conversation"
          >
            <MessageCircle size={20} />
          </button>
        )}
      </div>

      {primaryContact ? (
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-500">Name:</span>
            <span className="text-sm text-gray-900">
              {primaryContact.first_name} {primaryContact.last_name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-500">Email:</span>
            <span className="text-sm text-gray-900">{primaryContact.email_address}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-500">Phone:</span>
            <span className="text-sm text-gray-900">
              {primaryContact.country_code && primaryContact.phone_number
                ? `${primaryContact.country_code} ${primaryContact.phone_number}`
                : 'Not specified'
              }
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-500">Job Title:</span>
            <span className="text-sm text-gray-900">{primaryContact.job_title || 'Not specified'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-500">Department:</span>
            <span className="text-sm text-gray-900">{primaryContact.department || 'Not specified'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-500">Last Updated:</span>
            <span className="text-sm text-gray-900">{formatTimestampWithTimezone(primaryContact.updated_at)}</span>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-500">No primary contact specified</div>
      )}

      {/* Error Modal */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Cannot Start Conversation"
        message={errorMessage}
        showWarningIcon={true}
        actions={[
          {
            label: 'OK',
            onClick: () => setShowErrorModal(false),
            variant: 'primary'
          }
        ]}
      />
    </div>
  );
};

export default PrimaryContactCard;
